import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    AreaChart, Area, BarChart, Bar,
    PieChart, Pie, Cell, Tooltip, XAxis, YAxis,
    CartesianGrid, ResponsiveContainer
} from 'recharts';
import {
    FiGrid, FiUsers, FiLayers, FiActivity, FiDollarSign,
    FiTrendingUp, FiTrendingDown, FiRefreshCw, FiPackage,
    FiAlertTriangle, FiCheckCircle, FiPercent, FiBarChart2,
    FiDownload, FiCalendar, FiChevronDown
} from 'react-icons/fi';
import { apiFetch, downloadCsv } from '../utils/api';

// ── Palette ──────────────────────────────────────────────────────────────────
const PLAN_COLORS = {
    free:       '#6b7280',
    starter:    '#3b82f6',
    team:       '#8b5cf6',
    business:   '#f59e0b',
    enterprise: '#10b981',
};
const CHART_COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

// ── Composants utilitaires ────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
    <div className={`bg-gray-900 border border-gray-800 rounded-2xl p-5 ${className}`}>
        {children}
    </div>
);

const KpiCard = ({ label, value, sub, icon: Icon, color, trend, trendLabel }) => (
    <Card>
        <div className="flex items-start justify-between">
            <div>
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{label}</p>
                <p className="text-3xl font-bold text-white mt-1">{value}</p>
                {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
            </div>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                <Icon size={20} className="text-white" aria-hidden="true" />
            </div>
        </div>
        {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend >= 0 ? <FiTrendingUp size={13} aria-hidden="true" /> : <FiTrendingDown size={13} aria-hidden="true" />}
                {Math.abs(trend)}% {trendLabel}
            </div>
        )}
    </Card>
);

const SectionTitle = ({ children }) => (
    <h2 className="text-gray-200 font-semibold text-sm uppercase tracking-wider mb-4">{children}</h2>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 shadow-xl text-sm">
            <p className="text-gray-300 font-medium mb-1">{label}</p>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="text-gray-400">{p.name}:</span>
                    <span className="text-white font-medium">{typeof p.value === 'number' ? p.value.toLocaleString('fr-FR') : p.value}</span>
                </div>
            ))}
        </div>
    );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
    const [overview,   setOverview]   = useState(null);
    const [growth,     setGrowth]     = useState(null);
    const [mrr,        setMrr]        = useState(null);
    const [activity,   setActivity]   = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [months,     setMonths]     = useState(12);
    const [days,       setDays]       = useState(30);
    const [exporting,  setExporting]  = useState(null);
    const [exportOpen, setExportOpen] = useState(false);
    const exportRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (exportRef.current && !exportRef.current.contains(e.target)) {
                setExportOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, gr, mr, ac] = await Promise.all([
                apiFetch('/superadmin/overview'),
                apiFetch(`/superadmin/stats/growth?months=${months}`),
                apiFetch('/superadmin/stats/mrr'),
                apiFetch(`/superadmin/stats/activity?days=${days}`),
            ]);
            setOverview(ov);
            setGrowth(gr);
            setMrr(mr);
            setActivity(ac);
            setLastUpdate(new Date());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [months, days]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleExport = async (type, filename) => {
        setExporting(type);
        try {
            await downloadCsv(`/superadmin/export/${type}`, filename);
        } catch (e) { console.error(e); }
        finally { setExporting(null); }
    };

    // MRR trend (derniers 2 mois de croissance orgs)
    const mrrTrend = (() => {
        if (!growth?.series || growth.series.length < 2) return null;
        const last = growth.series[growth.series.length - 1]?.orgs || 0;
        const prev = growth.series[growth.series.length - 2]?.orgs || 0;
        if (!prev) return null;
        return Math.round(((last - prev) / prev) * 100);
    })();

    // Plan donut data
    const planDonut = mrr ? Object.entries(mrr.breakdown).map(([plan, data]) => ({
        name: plan, value: data.count, revenue: data.revenue
    })) : [];

    // MRR par plan (bar)
    const mrrBar = mrr ? Object.entries(mrr.breakdown)
        .filter(([, d]) => d.revenue > 0)
        .map(([plan, d]) => ({ plan, mrr: parseFloat(d.revenue.toFixed(2)), orgs: d.count })) : [];

    // KPIs calculés
    const paidOrgs      = mrr ? Object.entries(mrr.breakdown).filter(([p]) => p !== 'free').reduce((s, [, d]) => s + d.count, 0) : 0;
    const freeOrgs      = mrr ? (mrr.breakdown?.free?.count ?? 0) : 0;
    const totalOrgs     = overview?.totalOrgs || 0;
    const conversionRate = totalOrgs > 0 ? ((paidOrgs / totalOrgs) * 100).toFixed(1) : '0.0';
    const arpu           = paidOrgs > 0 && mrr ? (mrr.mrr / paidOrgs).toFixed(2) : '0.00';
    const userTrend      = (() => {
        if (!growth?.series || growth.series.length < 2) return null;
        const last = growth.series[growth.series.length - 1]?.users || 0;
        const prev = growth.series[growth.series.length - 2]?.users || 0;
        if (!prev) return null;
        return Math.round(((last - prev) / prev) * 100);
    })();

    return (
        <div className="p-4 sm:p-6 space-y-6 min-h-full">
            {/* En-tête */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start gap-3 sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                    <p className="text-gray-400 text-sm mt-0.5">
                        {lastUpdate ? `Mise à jour : ${lastUpdate.toLocaleTimeString('fr-FR')}` : 'Chargement...'}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {/* Sélecteur période croissance */}
                    <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg p-2">
                        <FiCalendar size={13} className="text-gray-500" aria-hidden="true" />
                        <select value={months} onChange={e => setMonths(Number(e.target.value))}
                            className="bg-transparent text-gray-300 text-sm focus:outline-none">
                            <option value={3}>3 mois</option>
                            <option value={6}>6 mois</option>
                            <option value={12}>12 mois</option>
                        </select>
                    </div>
                    {/* Sélecteur période activité */}
                    <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg p-2">
                        <FiActivity size={13} className="text-gray-500" aria-hidden="true" />
                        <select value={days} onChange={e => setDays(Number(e.target.value))}
                            className="bg-transparent text-gray-300 text-sm focus:outline-none">
                            <option value={7}>7 jours</option>
                            <option value={30}>30 jours</option>
                            <option value={90}>90 jours</option>
                        </select>
                    </div>
                    {/* Exports — dropdown */}
                    <div className="relative" ref={exportRef}>
                        <button
                            onClick={() => setExportOpen(o => !o)}
                            disabled={!!exporting}
                            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                        >
                            <FiDownload size={13} className={exporting ? 'animate-bounce' : ''} aria-hidden="true" />
                            Exporter
                            <FiChevronDown size={13} className={`transition-transform duration-150 ${exportOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                        </button>
                        {exportOpen && (
                            <div className="absolute right-0 top-full mt-1 z-20 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden min-w-[150px]">
                                {[
                                    { type: 'orgs',  label: 'Organisations', filename: 'organisations.csv' },
                                    { type: 'users', label: 'Utilisateurs',  filename: 'utilisateurs.csv'  },
                                    { type: 'logs',  label: 'Logs',          filename: 'logs.csv'          },
                                ].map(({ type, label, filename }) => (
                                    <button
                                        key={type}
                                        onClick={() => { handleExport(type, filename); setExportOpen(false); }}
                                        disabled={!!exporting}
                                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50"
                                    >
                                        <FiDownload size={12} className={exporting === type ? 'animate-bounce' : ''} aria-hidden="true" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={loadAll}
                        disabled={loading}
                        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                    >
                        <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
                        Actualiser
                    </button>
                </div>
            </div>

            {/* KPIs principaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard
                    label="Organisations" icon={FiLayers} color="bg-brand-600"
                    value={overview?.totalOrgs?.toLocaleString('fr-FR') ?? '—'}
                    sub={`${overview?.statusDistribution?.active ?? 0} actives · ${overview?.statusDistribution?.suspended ?? 0} suspendues`}
                    trend={mrrTrend} trendLabel="vs mois dernier"
                />
                <KpiCard
                    label="Utilisateurs" icon={FiUsers} color="bg-purple-600"
                    value={overview?.totalUsers?.toLocaleString('fr-FR') ?? '—'}
                    sub={`${overview?.usageTotals?.totalUsers ?? 0} slots actifs`}
                    trend={userTrend} trendLabel="vs mois dernier"
                />
                <KpiCard
                    label="MRR estimé" icon={FiDollarSign} color="bg-emerald-600"
                    value={mrr ? `${mrr.mrr.toFixed(2)} €` : '—'}
                    sub={`ARPU : ${arpu} €`}
                />
                <KpiCard
                    label="Events (30j)" icon={FiActivity} color="bg-amber-600"
                    value={activity ? activity.series.reduce((a, b) => a + b.count, 0).toLocaleString('fr-FR') : '—'}
                    sub={`${overview?.totalLogs?.toLocaleString('fr-FR') ?? 0} logs total`}
                />
            </div>

            {/* KPIs secondaires */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                        <FiPackage size={17} className="text-blue-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Messages totaux</p>
                        <p className="text-white font-bold text-lg">{(overview?.usageTotals?.totalMessages ?? 0).toLocaleString('fr-FR')}</p>
                    </div>
                </Card>
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
                        <FiGrid size={17} className="text-teal-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Todos totaux</p>
                        <p className="text-white font-bold text-lg">{(overview?.usageTotals?.totalTodos ?? 0).toLocaleString('fr-FR')}</p>
                    </div>
                </Card>
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <FiPercent size={17} className="text-indigo-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Taux conversion</p>
                        <p className="text-white font-bold text-lg">{conversionRate} %</p>
                        <p className="text-gray-600 text-xs">{paidOrgs} payants / {freeOrgs} gratuits</p>
                    </div>
                </Card>
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                        <FiAlertTriangle size={17} className="text-amber-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Org suspendues</p>
                        <p className="text-white font-bold text-lg">{overview?.statusDistribution?.suspended ?? 0}</p>
                    </div>
                </Card>
            </div>

            {/* KPIs financiers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <FiBarChart2 size={17} className="text-emerald-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">ARPU (rev / org payante)</p>
                        <p className="text-white font-bold text-lg">{arpu} €</p>
                    </div>
                </Card>
                <Card className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0">
                        <FiCheckCircle size={17} className="text-green-400" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Plans payants actifs</p>
                        <p className="text-white font-bold text-lg">{paidOrgs}</p>
                    </div>
                </Card>
                {mrr && Object.entries(mrr.breakdown)
                    .filter(([p]) => p !== 'free' && mrr.breakdown[p]?.count > 0)
                    .slice(0, 2)
                    .map(([plan, d]) => (
                        <Card key={plan} className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                                style={{ background: (PLAN_COLORS[plan] || '#6366f1') + '15' }}>
                                <FiDollarSign size={17} style={{ color: PLAN_COLORS[plan] || '#6366f1' }} aria-hidden="true" />
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs capitalize">{plan} MRR</p>
                                <p className="text-white font-bold text-lg">{d.revenue.toFixed(0)} €</p>
                                <p className="text-gray-600 text-xs">{d.count} org{d.count > 1 ? 's' : ''}</p>
                            </div>
                        </Card>
                    ))
                }
            </div>

            {/* Croissance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <SectionTitle>Croissance mensuelle — Organisations</SectionTitle>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={growth?.series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gradOrgs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                            <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="orgs" name="Nouvelles orgs" stroke="#6366f1" strokeWidth={2} fill="url(#gradOrgs)" dot={{ fill: '#6366f1', r: 3 }} activeDot={{ r: 5 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                <Card>
                    <SectionTitle>Croissance mensuelle — Utilisateurs</SectionTitle>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={growth?.series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                            <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="users" name="Nouveaux users" stroke="#8b5cf6" strokeWidth={2} fill="url(#gradUsers)" dot={{ fill: '#8b5cf6', r: 3 }} activeDot={{ r: 5 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>
            </div>

            {/* MRR + Plans */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* MRR par plan */}
                <Card className="lg:col-span-2">
                    <SectionTitle>MRR par plan (€/mois)</SectionTitle>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={mrrBar} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                            <XAxis dataKey="plan" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="mrr" name="MRR (€)" radius={[6, 6, 0, 0]}>
                                {mrrBar.map((entry) => (
                                    <Cell key={entry.plan} fill={PLAN_COLORS[entry.plan] || '#6366f1'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Donut plans */}
                <Card>
                    <SectionTitle>Répartition des plans</SectionTitle>
                    <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                            <Pie
                                data={planDonut}
                                cx="50%" cy="50%"
                                innerRadius={45} outerRadius={70}
                                paddingAngle={3}
                                dataKey="value"
                            >
                                {planDonut.map((entry) => (
                                    <Cell key={entry.name} fill={PLAN_COLORS[entry.name] || '#6366f1'} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 mt-2">
                        {planDonut.map(p => (
                            <div key={p.name} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: PLAN_COLORS[p.name] || '#6366f1' }} />
                                    <span className="text-gray-400 capitalize">{p.name}</span>
                                </div>
                                <span className="text-white font-medium">{p.value}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Activité 30 jours */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2">
                    <SectionTitle>Activité quotidienne — 30 derniers jours</SectionTitle>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={activity?.series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gradActivity" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                            <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false}
                                interval={4} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="count" name="Événements" stroke="#06b6d4" strokeWidth={2} fill="url(#gradActivity)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                {/* Top actions */}
                <Card>
                    <SectionTitle>Top actions (30j)</SectionTitle>
                    <div className="space-y-2">
                        {(activity?.topActions || []).map((a, i) => {
                            const max = activity.topActions[0]?.count || 1;
                            const pct = Math.round((a.count / max) * 100);
                            return (
                                <div key={a.action}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="text-gray-300 font-mono truncate max-w-[160px]">{a.action}</span>
                                        <span className="text-gray-400 font-medium">{a.count}</span>
                                    </div>
                                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            {/* Dernières activités */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <SectionTitle>Dernières organisations créées</SectionTitle>
                    <div className="space-y-2">
                        {(overview?.recentOrgs || []).map(o => (
                            <div key={o._id} className="flex items-center justify-between text-sm py-2 border-b border-gray-800 last:border-0">
                                <div>
                                    <span className="text-white font-medium">{o.name}</span>
                                    <span className="text-gray-500 text-xs ml-2">/{o.slug}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                        style={{ background: PLAN_COLORS[o.plan] + '20', color: PLAN_COLORS[o.plan] }}>
                                        {o.plan}
                                    </span>
                                    <span className="text-gray-500 text-xs">{new Date(o.createdAt).toLocaleDateString('fr-FR')}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card>
                    <SectionTitle>Derniers utilisateurs inscrits</SectionTitle>
                    <div className="space-y-2">
                        {(overview?.recentUsers || []).map(u => (
                            <div key={u._id} className="flex items-center gap-3 py-2 border-b border-gray-800 last:border-0">
                                <div className="w-7 h-7 rounded-full bg-brand-600/20 flex items-center justify-center text-brand-400 text-xs font-bold shrink-0">
                                    {(u.name || '?')[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white text-sm font-medium truncate">{u.name}</div>
                                    <div className="text-gray-500 text-xs truncate">{u.email}</div>
                                </div>
                                <span className="text-gray-500 text-xs shrink-0">{new Date(u.createdAt).toLocaleDateString('fr-FR')}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
